export const VIEW_AS_CONFIRM_HEADER = "X-View-As-Write-Confirm";

const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

let isInstalled = false;
let originalFetch: typeof window.fetch | null = null;

const parseStoredUser = () => {
  if (typeof window === "undefined" || !window.localStorage) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem("user");
    return raw ? JSON.parse(raw) : {};
  } catch (_error) {
    return {};
  }
};

const isViewAsActive = () => {
  const user = parseStoredUser();
  return Boolean(user?.view_as?.active);
};

const resolvePathname = (input: RequestInfo | URL) => {
  try {
    if (input instanceof URL) {
      return input.pathname;
    }

    if (typeof input === "string") {
      return new URL(input, window.location.origin).pathname;
    }

    return new URL(input.url, window.location.origin).pathname;
  } catch (_error) {
    return "";
  }
};

const isViewAsWriteExemptPath = (pathname: string) =>
  pathname === "/api/auth/signin" ||
  pathname === "/api/auth/signin-tlc" ||
  pathname === "/api/auth/signout" ||
  pathname === "/api/auth/view-as" ||
  pathname.startsWith("/api/auth/view-as/") ||
  pathname === "/api/auth/super-admin-context" ||
  pathname.startsWith("/api/auth/super-admin-context/");

const extractMethod = (input: RequestInfo | URL, init?: RequestInit) => {
  const fromInit = `${init?.method || ""}`.trim();
  if (fromInit.length > 0) {
    return fromInit.toUpperCase();
  }

  if (typeof input === "object" && "method" in input) {
    return `${input.method || "GET"}`.toUpperCase();
  }

  return "GET";
};

const mergeHeaders = (input: RequestInfo | URL, init?: RequestInit) => {
  const headers = new Headers(
    typeof input === "object" && "headers" in input ? input.headers : undefined,
  );

  if (init?.headers) {
    const initHeaders = new Headers(init.headers);
    initHeaders.forEach((value, key) => {
      headers.set(key, value);
    });
  }

  return headers;
};

const isConfirmationAlreadyPresent = (headers: Headers) =>
  headers.get(VIEW_AS_CONFIRM_HEADER) === "1";

const buildWriteConfirmMessage = (pathname: string) =>
  `View-as mode is active. Confirm write action for ${pathname || "this request"}?`;

export const installViewAsFetchGuard = () => {
  if (typeof window === "undefined" || typeof window.fetch !== "function") {
    return;
  }

  if (isInstalled) {
    return;
  }

  originalFetch = window.fetch.bind(window);
  const guardedFetch: typeof window.fetch = async (input, init) => {
    const method = extractMethod(input, init);
    if (!MUTATING_METHODS.has(method)) {
      return originalFetch!(input, init);
    }

    if (!isViewAsActive()) {
      return originalFetch!(input, init);
    }

    const pathname = resolvePathname(input);
    if (isViewAsWriteExemptPath(pathname)) {
      return originalFetch!(input, init);
    }

    const headers = mergeHeaders(input, init);
    if (isConfirmationAlreadyPresent(headers)) {
      return originalFetch!(input, init);
    }

    const confirmed = window.confirm(buildWriteConfirmMessage(pathname));
    if (!confirmed) {
      const error = new Error("View-as write action cancelled by user");
      error.name = "AbortError";
      throw error;
    }

    headers.set(VIEW_AS_CONFIRM_HEADER, "1");

    return originalFetch!(input, {
      ...(init || {}),
      headers,
    });
  };

  window.fetch = guardedFetch;
  isInstalled = true;
};

export const uninstallViewAsFetchGuard = () => {
  if (!isInstalled || !originalFetch || typeof window === "undefined") {
    return;
  }

  window.fetch = originalFetch;
  originalFetch = null;
  isInstalled = false;
};
